import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import ButtonComponent from '../../../components/formlib/ButtonComponent';
import { Box } from '@mui/system';
import MediumTypography from '../../../components/formlib/MediumTypography';
import ModalPopup from '../../../components/formlib/ModalPopup';
import InformationModal from '../../ifsp/InformationModal';
import { ReactComponent as DownArrow } from '../../../assets/images/downarrowcolored.svg';
import { ReactComponent as UpArrow } from '../../../assets/images/uparrowcolored.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/infoIcon.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/images/deleteAlertIcon.svg';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import CommentTextbox from '../../../components/formlib/CommentTextbox';
import { LoaderContext, LoaderContextType } from '../../../layouts/AppSidebar';
import { AlertColor } from '@mui/material';
import SnackBarComponent from '../../../components/formlib/SnackBarComponent';
import {
  addOrUpdateIfspDevelopmentSummaryReport,
  deleteIfspDevelopmentalSummary,
  getIfspDevelopmentSummaryReport,
} from '../../../services/ifspClient/IfspApi';
import {
  setIfspDevelopmentSummaryReport,
  updateIfspDevelopmentSummaryReport,
  updateOriginalIfspDevelopmentSummaryReport,
} from '../../../redux/features/ifsp/ifspDevelopmentSummarySlice';
import dayjs from 'dayjs';
import { useIfspUnsavedChanges } from '../ifspUnsavedChanges';
import IfspHeld from '../ifspComponents/IfspHeld';

const DevelopmentSummaryReport: FC = () => {
  const ifspDevelopmentSummaryReport = useAppSelector(
    (state) =>
      state.ifspDevelopmentSummaryReportDetails.ifspDevelopmentSummaryReport,
  );

  const ifspOriginalDevelopmentSummaryReport = useAppSelector(
    (state) =>
      state.ifspDevelopmentSummaryReportDetails
        .originalIfspDevelopmentSummaryReport,
  );

  const ifspPermission = useAppSelector((state) => state.ifspPermissionDetails);
  const dispatch = useAppDispatch();
  const [openIndex, setOpenIndex] = useState<number>(0);
  const [openAccordianIndex, setOpenAccordianIndex] = useState<number>(0);
  const [openAccordian, setOpenAccordian] = useState<boolean>(false);
  const [deleteNewSection, setDeleteNewSection] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [toastrVariable, setToastrVariable] = useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [toastrId, setToastrId] = useState('');
  const [infoModal, setInfoModal] = useState(false);
  const [openUnSavedModal, setOpenUnSavedModal] = useState<boolean>(false);
  const [transitionRenderKey, setTransitionRenderKey] = useState(0);
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const ifspHeatBeatDetails = useAppSelector(
    (state) => state.ifspHeatBeatDetails,
  );

  const parentRef = useRef<HTMLDivElement>(null);
  const {
    isDevelopmentalSummaryChanged,
    isDevelopmentalSummaryChangedAndNoCreatedDate,
  } = useIfspUnsavedChanges();

  useEffect(() => {
    toggleLoader(true);
    getIfspDevelopmentalSummary();
  }, [ifspHeatBeatDetails.developmentalSummaryEmpId]);

  const getIfspDevelopmentalSummary = () => {
    getIfspDevelopmentSummaryReport()
      .then((response) => {
        if (response) {
          toggleLoader(false);
          dispatch(setIfspDevelopmentSummaryReport(response));
        }
      })
      .catch(() => {
        setOpen(true);
        setToastrVariable('error');
        setToastrId('IFSP.DevelopmentSummary.FetchError');
        setToastrDefaultMessage('Failed to fetch developmental summary');
        toggleLoader(false);
      });
  };

  const addSubSection = () => {
    dispatch(
      updateIfspDevelopmentSummaryReport({
        ...ifspDevelopmentSummaryReport,
        developmentalSummary: [
          ...ifspDevelopmentSummaryReport.developmentalSummary,
          {
            id: '',
            adaptiveComment: '',
            socialEmotionalComment: '',
            cognitionComment: '',
            communicationComment: '',
            motorComment: '',
            sessionReviewedDate: '',
          },
        ],
      }),
    );
    setOpenIndex(openIndex + 1);
  };

  const createNewSection = () => {
    dispatch(
      setIfspDevelopmentSummaryReport({
        ...ifspDevelopmentSummaryReport,
        developmentalSummary: [
          ...ifspDevelopmentSummaryReport.developmentalSummary,
          {
            adaptiveComment: '',
            socialEmotionalComment: '',
            cognitionComment: '',
            communicationComment: '',
            motorComment: '',
            id: '',
            sessionReviewedDate: '',
          },
        ],
      }),
    );
    dispatch(
      updateOriginalIfspDevelopmentSummaryReport({
        ...ifspOriginalDevelopmentSummaryReport,
        developmentalSummary: [
          ...ifspOriginalDevelopmentSummaryReport.developmentalSummary,
          {
            adaptiveComment: '',
            socialEmotionalComment: '',
            cognitionComment: '',
            communicationComment: '',
            motorComment: '',
            id: '',
            sessionReviewedDate: '',
          },
        ],
      }),
    );
  };

  const deleteSection = () => {
    toggleLoader(true);
    const summaryId =
      ifspDevelopmentSummaryReport.developmentalSummary[
        ifspDevelopmentSummaryReport.developmentalSummary.length - 1
      ].id;
    if (summaryId) {
      setOpen(true);
      setToastrVariable('success');
      setToastrId('IFSP.DevelopmentalSummary.DeleteSuccess');
      setToastrDefaultMessage('Developmental Summary deleted successfully');
      deleteIfspDevelopmentalSummary(summaryId)
        .then(() => {
          dispatch(
            updateOriginalIfspDevelopmentSummaryReport({
              developmentalSummary:
                ifspOriginalDevelopmentSummaryReport.developmentalSummary.slice(
                  0,
                  -1,
                ),
            }),
          );
          dispatch(
            updateIfspDevelopmentSummaryReport({
              ...ifspDevelopmentSummaryReport,
              developmentalSummary:
                ifspDevelopmentSummaryReport.developmentalSummary.slice(0, -1),
            }),
          );
        })
        .catch(() => {
          setOpen(true);
          setToastrVariable('error');
          setToastrId('IFSP.DevelopmentalSummary.DeleteError');
          setToastrDefaultMessage('Failed to delete Developmental Summary');
        })
        .finally(() => {
          setDeleteNewSection(false);
          toggleLoader(false);
        });
    } else {
      dispatch(
        updateOriginalIfspDevelopmentSummaryReport({
          developmentalSummary:
            ifspOriginalDevelopmentSummaryReport.developmentalSummary.slice(
              0,
              -1,
            ),
        }),
      );
      dispatch(
        updateIfspDevelopmentSummaryReport({
          ...ifspDevelopmentSummaryReport,
          developmentalSummary:
            ifspDevelopmentSummaryReport.developmentalSummary.slice(0, -1),
        }),
      );
      setDeleteNewSection(false);
      toggleLoader(false);
    }
  };

  const handleCloseSnakBar = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const handleOpenInfoModal = () => {
    setInfoModal(true);
  };

  const handleCloseInfoModal = () => {
    setInfoModal(false);
  };

  const canShowDeleteSectionDevelopmental = (mainIndex: number): boolean => {
    const lastIndex =
      ifspDevelopmentSummaryReport.developmentalSummary.length - 1;
    const lastItem =
      ifspDevelopmentSummaryReport.developmentalSummary[lastIndex];

    return (
      ifspPermission.hasClientEditPermission &&
      !lastItem.sessionReviewedDate &&
      mainIndex === lastIndex &&
      mainIndex > 0
    );
  };

  const addOrUpdateIfspReportDetails = () => {
    toggleLoader(true);
    addOrUpdateIfspDevelopmentSummaryReport({
      ...ifspDevelopmentSummaryReport,
      developmentalSummary:
        ifspDevelopmentSummaryReport.developmentalSummary.map((item, index) => {
          const isLastIndex =
            ifspDevelopmentSummaryReport.developmentalSummary.length - 1 ===
            index;
          return {
            ...item,
            sessionReviewedDate:
              isLastIndex &&
              isDevelopmentalSummaryChangedAndNoCreatedDate &&
              !item.sessionReviewedDate
                ? dayjs().format('MM/DD/YYYY hh:mm A')
                : item.sessionReviewedDate,
          };
        }),
    })
      .then(() => {
        setOpen(true);
        setToastrVariable('success');
        setToastrId('IFSP.DevelopmentalSummary.UpdateSuccess');
        setToastrDefaultMessage(
          'IFSP Developmental Summary updated successfully',
        );
        return getIfspDevelopmentalSummary();
      })
      .catch(() => {
        setOpen(true);
        setToastrVariable('error');
        setToastrId('IFSP.DevelopmentalSummary.UpdateError');
        setToastrDefaultMessage('Failed to update IFSP Developmental Summary');
      })
      .finally(() => toggleLoader(false));
  };

  return (
    <Box className="ml-md mr-md mb-md">
      {ifspDevelopmentSummaryReport.isCurrentlyHeld ? (
        <Box sx={{ position: 'relative' }}>
          <IfspHeld ifspHealBy={ifspDevelopmentSummaryReport?.heldBy} />
        </Box>
      ) : null}
      <Box
        ref={parentRef}
        style={{
          pointerEvents: ifspDevelopmentSummaryReport.isCurrentlyHeld
            ? 'none'
            : 'auto',
          cursor: ifspDevelopmentSummaryReport.isCurrentlyHeld
            ? 'not-allowed'
            : 'default',
          opacity: ifspDevelopmentSummaryReport.isCurrentlyHeld ? '0.5' : '1 ',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onKeyDownCapture={(e) => {
          if (ifspDevelopmentSummaryReport.isCurrentlyHeld) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onFocus={() => {
          if (ifspDevelopmentSummaryReport.isCurrentlyHeld) {
            if (parentRef.current) parentRef.current.focus();
          }
        }}
      >
        <Box
          sx={{
            border: '1px solid #008C82',
            borderBottomLeftRadius: '4px',
            borderBottomRightRadius: '4px',
            borderTop: 'none',
          }}
          className="p-sm"
        >
          <InformationModal
            open={infoModal}
            titleLabel="IFSP.Information"
            titleDefaultLabel="Information"
            buttonLabel="IFSP.InfoOkButton"
            buttonDefaultLabel="Ok"
            onClose={handleCloseInfoModal}
            infoLabelId="IFSP.DevelopmentalSummaryInfo"
            infoDefaultLabel="This page describes the child's current level of functioning. Information collected throughout the evaluation and assessment process should be summarized here and must include information about the five developmental domains. Information may include results from supplemental assessments. For each domain, provide statement of how child's present level of development impacts their participation in daily activities. This may include results from other evaluations and functional assessments."
          />
          <SnackBarComponent
            open={open}
            handleClose={handleCloseSnakBar}
            successOrError={toastrVariable}
            labelId={toastrId}
            defaultMessageId={toastrDefaultMessage}
          />
          <ModalPopup
            open={openUnSavedModal}
            description="UnsavedAlertDescriptionWhileLeaving"
            labelId1="Clientpage.cancelbtn"
            negativeActionLabel="Cancel"
            labelId2="Clientpage.Okbtn"
            positiveActionLabel="Ok"
            onCancel={() => {
              setOpenUnSavedModal(false);
            }}
            onOk={() => {
              dispatch(
                setIfspDevelopmentSummaryReport(
                  ifspOriginalDevelopmentSummaryReport,
                ),
              );
              setTransitionRenderKey((prev) => prev + 1);
              setTimeout(() => {
                setOpenUnSavedModal(false);
              }, 500);
            }}
          />
          <ModalPopup
            open={deleteNewSection}
            description="permanentDeleteConfirmationMessage"
            onOk={() => {
              deleteSection();
            }}
            onCancel={() => {
              setDeleteNewSection(false);
            }}
            labelId1="Clientpage.cancelbtn"
            negativeActionLabel="cancelText"
            labelId2="Clientpage.Okbtn"
            positiveActionLabel="Ok"
          />
          {ifspDevelopmentSummaryReport.developmentalSummary.map(
            (ifspData, mainIndex) => (
              <Box
                key={(mainIndex + 1).toString()}
                component="section"
                className="formCardview borderRadius4 mb-lg"
                sx={{
                  border: '1px solid #00C6B8',
                  backgroundColor: '#ECF9F8',
                }}
              >
                <Box component="div" className="flex__justify__space-between">
                  <Box component="div" className="flex__ align__items__center">
                    <MediumTypography
                      labelid="IFSP.session5"
                      defaultlabel="Developmental Summary"
                      fontSize="18px"
                      fontweight={600}
                      textColor="#2A4241"
                    />
                    {ifspData.sessionReviewedDate ? (
                      <Box
                        sx={{
                          display: 'flex',
                          padding: '4px 8px 4px 8px',
                          borderRadius: '4px',
                          marginLeft: '24px',
                          backgroundColor: '#97A6A5',
                          alignItems: 'center',
                        }}
                      >
                        <MediumTypography
                          labelid="IFSP.UpdatedOn"
                          defaultlabel="Created On: "
                          fontSize="14px"
                          fontweight={400}
                          textColor="#fff"
                        />
                        <MediumTypography
                          fontSize="14px"
                          fontweight={400}
                          textColor="#fff"
                          className="pl-xs"
                          label={ifspData.sessionReviewedDate}
                        />
                      </Box>
                    ) : null}
                  </Box>
                  {openAccordian && mainIndex === openAccordianIndex ? (
                    <Box className="flex__ align__items__center">
                      {ifspPermission.hasClientEditPermission &&
                      ifspData.sessionReviewedDate &&
                      mainIndex ===
                        ifspDevelopmentSummaryReport.developmentalSummary
                          .length -
                          1 ? (
                        <Box
                          className="cursorPointer"
                          sx={{
                            padding: '4px 8px 4px 8px',
                            borderRadius: '4px',
                            marginRight: '24px',
                            border: '1px solid #008C82',
                            backgroundColor: '#fff',
                          }}
                          onClick={() => {
                            createNewSection();
                          }}
                        >
                          <MediumTypography
                            labelid="IFSP.AddNewSection"
                            defaultlabel="+ Add New"
                            fontSize="12px"
                            fontweight={600}
                          />
                        </Box>
                      ) : (
                        canShowDeleteSectionDevelopmental(mainIndex) && (
                          <Box
                            component={'div'}
                            className="cursorPointer flex__ align__items__center pr-md"
                            onClick={() => {
                              setDeleteNewSection(true);
                            }}
                            sx={{
                              padding: '4px 8px 4px 8px',
                              borderRadius: '4px',
                              marginRight: '24px',
                              border: '1px solid #EB4C60',
                              backgroundColor: '#fff',
                            }}
                          >
                            <DeleteIcon className="pr-xs" />
                            <MediumTypography
                              labelid="IFSP.DeleteNewSection"
                              defaultlabel="Delete"
                              fontSize="12px"
                              fontweight={600}
                            />
                          </Box>
                        )
                      )}
                      <UpArrow
                        className="cursorPointer"
                        onClick={() => {
                          setOpenAccordianIndex((prev) => prev);
                          setOpenAccordian(false);
                        }}
                      />
                    </Box>
                  ) : (
                    <Box className="flex__ align__items__center">
                      {ifspPermission.hasClientEditPermission &&
                      ifspDevelopmentSummaryReport.developmentalSummary[
                        ifspDevelopmentSummaryReport.developmentalSummary
                          .length - 1
                      ].sessionReviewedDate &&
                      mainIndex ===
                        ifspDevelopmentSummaryReport.developmentalSummary
                          .length -
                          1 ? (
                        <Box
                          className="cursorPointer"
                          sx={{
                            padding: '4px 8px 4px 8px',
                            borderRadius: '4px',
                            marginRight: '24px',
                            border: '1px solid #008C82',
                            backgroundColor: '#fff',
                          }}
                          onClick={() => {
                            createNewSection();
                          }}
                        >
                          <MediumTypography
                            labelid="IFSP.AddNewSection"
                            defaultlabel="+ Add New"
                            fontSize="12px"
                            fontweight={600}
                          />
                        </Box>
                      ) : (
                        canShowDeleteSectionDevelopmental(mainIndex) && (
                          <Box
                            component={'div'}
                            className="cursorPointer flex__ align__items__center pr-md"
                            onClick={() => {
                              setDeleteNewSection(true);
                            }}
                            sx={{
                              padding: '4px 8px 4px 8px',
                              borderRadius: '4px',
                              marginRight: '24px',
                              border: '1px solid #EB4C60',
                              backgroundColor: '#fff',
                            }}
                          >
                            <DeleteIcon className="pr-xs" />
                            <MediumTypography
                              labelid="IFSP.DeleteNewSection"
                              defaultlabel="Delete"
                              fontSize="12px"
                              fontweight={600}
                            />
                          </Box>
                        )
                      )}
                      <DownArrow
                        className="cursorPointer"
                        onClick={() => {
                          setOpenAccordianIndex(mainIndex);
                          setOpenAccordian(true);
                        }}
                      />
                    </Box>
                  )}
                </Box>
                {openAccordian && mainIndex === openAccordianIndex && (
                  <Box key={transitionRenderKey}>
                    <Box
                      ref={parentRef}
                      style={{
                        pointerEvents: !ifspPermission.hasClientEditPermission
                          ? 'none'
                          : 'auto',
                        opacity: !ifspPermission.hasClientEditPermission
                          ? '0.5'
                          : '1 ',
                      }}
                      onKeyDownCapture={(e) => {
                        if (!ifspPermission.hasClientEditPermission) {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      }}
                      onFocus={() => {
                        if (!ifspPermission.hasClientEditPermission) {
                          if (parentRef.current) parentRef.current.focus();
                        }
                      }}
                    >
                      <Box
                        component="div"
                        sx={{
                          margin: '24px 0px 0px 0px',
                        }}
                      >
                        <Box
                          component="div"
                          className="flex__justify__space-between mt-lg mb-lg"
                        >
                          <Box
                            component="div"
                            className="flex__ align__items__center"
                          >
                            <MediumTypography
                              labelid="IFSP.summary"
                              defaultlabel="Summary of Development"
                              textColor="#2A4241"
                              fontweight={600}
                              fontSize="16px"
                              className="mr-sm"
                            />
                            <InfoIcon
                              className="cursorPointer"
                              onClick={handleOpenInfoModal}
                            />
                          </Box>
                        </Box>
                        <Box
                          sx={{ alignItems: 'center', display: 'inline-flex' }}
                        >
                          <MediumTypography
                            sxProps={{
                              marginLeft: '4px',
                              marginRight: '24px',
                              fontFamily: 'Lato-Regular',
                              fontSize: 14,
                              fontWeight: 500,
                            }}
                            labelid={'IFSP.adaptive'}
                            defaultlabel={'Adaptive:'}
                          />
                        </Box>
                        <Box sx={{ paddingTop: '8px' }}>
                          <CommentTextbox
                            className="translate"
                            name={'concern'}
                            placeholder={'Enter your comments'}
                            rows={5}
                            maxLength={5000}
                            Value={
                              ifspDevelopmentSummaryReport.developmentalSummary[
                                mainIndex
                              ].adaptiveComment
                            }
                            handlechange={(value) => {
                              dispatch(
                                updateIfspDevelopmentSummaryReport({
                                  ...ifspDevelopmentSummaryReport,
                                  developmentalSummary:
                                    ifspDevelopmentSummaryReport.developmentalSummary.map(
                                      (item, index) => {
                                        if (index === mainIndex) {
                                          return {
                                            ...item,
                                            adaptiveComment: value || null,
                                          };
                                        }
                                        return item;
                                      },
                                    ),
                                }),
                              );
                            }}
                            style={{
                              width: '840px',
                            }}
                          />
                        </Box>

                        <Box
                          sx={{ alignItems: 'center', display: 'inline-flex' }}
                        >
                          <MediumTypography
                            sxProps={{
                              marginLeft: '4px',
                              marginRight: '24px',
                              fontFamily: 'Lato-Regular',
                              fontSize: 14,
                              fontWeight: 500,
                            }}
                            labelid={'IFSP.personal-Social'}
                            defaultlabel={'Social-Emotional:'}
                          />
                        </Box>
                        <Box sx={{ paddingTop: '8px' }}>
                          <CommentTextbox
                            className="translate"
                            name={'concern'}
                            placeholder={'Enter your comments'}
                            rows={5}
                            maxLength={5000}
                            Value={
                              ifspDevelopmentSummaryReport.developmentalSummary[
                                mainIndex
                              ].socialEmotionalComment
                            }
                            handlechange={(value) => {
                              dispatch(
                                updateIfspDevelopmentSummaryReport({
                                  ...ifspDevelopmentSummaryReport,
                                  developmentalSummary:
                                    ifspDevelopmentSummaryReport.developmentalSummary.map(
                                      (item, index) => {
                                        if (index === mainIndex) {
                                          return {
                                            ...item,
                                            socialEmotionalComment:
                                              value || null,
                                          };
                                        }
                                        return item;
                                      },
                                    ),
                                }),
                              );
                            }}
                            style={{
                              width: '840px',
                            }}
                          />
                        </Box>

                        <Box
                          sx={{ alignItems: 'center', display: 'inline-flex' }}
                        >
                          <MediumTypography
                            sxProps={{
                              marginLeft: '4px',
                              marginRight: '24px',
                              fontFamily: 'Lato-Regular',
                              fontSize: 14,
                              fontWeight: 500,
                            }}
                            labelid={'IFSP.communication'}
                            defaultlabel={'Communication:'}
                          />
                        </Box>
                        <Box sx={{ paddingTop: '8px' }}>
                          <CommentTextbox
                            className="translate"
                            name={'concern'}
                            placeholder={'Enter your comments'}
                            rows={5}
                            maxLength={5000}
                            Value={
                              ifspDevelopmentSummaryReport.developmentalSummary[
                                mainIndex
                              ].communicationComment
                            }
                            handlechange={(value) => {
                              dispatch(
                                updateIfspDevelopmentSummaryReport({
                                  ...ifspDevelopmentSummaryReport,
                                  developmentalSummary:
                                    ifspDevelopmentSummaryReport.developmentalSummary.map(
                                      (item, index) => {
                                        if (index === mainIndex) {
                                          return {
                                            ...item,
                                            communicationComment: value || null,
                                          };
                                        }
                                        return item;
                                      },
                                    ),
                                }),
                              );
                            }}
                            style={{
                              width: '840px',
                            }}
                          />
                        </Box>

                        <Box
                          sx={{ alignItems: 'center', display: 'inline-flex' }}
                        >
                          <MediumTypography
                            sxProps={{
                              marginLeft: '4px',
                              marginRight: '24px',
                              fontFamily: 'Lato-Regular',
                              fontSize: 14,
                              fontWeight: 500,
                            }}
                            labelid={'IFSP.motor'}
                            defaultlabel={'Motor:'}
                          />
                        </Box>
                        <Box sx={{ paddingTop: '8px' }}>
                          <CommentTextbox
                            className="translate"
                            name={'concern'}
                            placeholder={'Enter your comments'}
                            rows={5}
                            maxLength={5000}
                            Value={
                              ifspDevelopmentSummaryReport.developmentalSummary[
                                mainIndex
                              ].motorComment
                            }
                            handlechange={(value) => {
                              dispatch(
                                updateIfspDevelopmentSummaryReport({
                                  ...ifspDevelopmentSummaryReport,
                                  developmentalSummary:
                                    ifspDevelopmentSummaryReport.developmentalSummary.map(
                                      (item, index) => {
                                        if (index === mainIndex) {
                                          return {
                                            ...item,
                                            motorComment: value || null,
                                          };
                                        }
                                        return item;
                                      },
                                    ),
                                }),
                              );
                            }}
                            style={{
                              width: '840px',
                            }}
                          />
                        </Box>

                        <Box
                          sx={{ alignItems: 'center', display: 'inline-flex' }}
                        >
                          <MediumTypography
                            sxProps={{
                              marginLeft: '4px',
                              marginRight: '24px',
                              fontFamily: 'Lato-Regular',
                              fontSize: 14,
                              fontWeight: 500,
                            }}
                            labelid={'IFSP.cognition'}
                            defaultlabel={'"Cognition:"'}
                          />
                        </Box>
                        <Box sx={{ paddingTop: '8px' }}>
                          <CommentTextbox
                            className="translate"
                            name={'concern'}
                            placeholder={'Enter your comments'}
                            rows={5}
                            maxLength={5000}
                            Value={
                              ifspDevelopmentSummaryReport.developmentalSummary[
                                mainIndex
                              ].cognitionComment
                            }
                            handlechange={(value) => {
                              dispatch(
                                updateIfspDevelopmentSummaryReport({
                                  ...ifspDevelopmentSummaryReport,
                                  developmentalSummary:
                                    ifspDevelopmentSummaryReport.developmentalSummary.map(
                                      (item, index) => {
                                        if (index === mainIndex) {
                                          return {
                                            ...item,
                                            cognitionComment: value || null,
                                          };
                                        }
                                        return item;
                                      },
                                    ),
                                }),
                              );
                            }}
                            style={{
                              width: '840px',
                            }}
                          />
                        </Box>
                      </Box>
                      <ButtonComponent
                        sxProps={{ display: 'none' }}
                        className="btn-primary btn-submit mt-lg"
                        variantType="contained"
                        type="submit"
                        labelId="IFSP.addAdditionalEvaluation"
                        defaultLabelId="Add Additional Evaluation"
                        onClick={() => {
                          addSubSection();
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            ),
          )}
          <Box component="div" className="flex__ justifyContent-FlexEnd mb-sm">
            <ButtonComponent
              className="btn-primary btn-cancel"
              labelId={'cancelText'}
              defaultLabelId="Cancel"
              variantType="outlined"
              onClick={() => {
                if (isDevelopmentalSummaryChanged) {
                  setOpenUnSavedModal(true);
                } else {
                  setOpenUnSavedModal(false);
                }
              }}
              disabled={!ifspPermission.hasClientEditPermission}
            />
            <ButtonComponent
              className="btn-primary btn-submit  ml-md"
              labelId="IFSP.saveDevelopmentalSummary"
              defaultLabelId="Save Developmental Summary"
              variantType="contained"
              onClick={() => {
                addOrUpdateIfspReportDetails();
              }}
              disabled={!ifspPermission.hasClientEditPermission}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DevelopmentSummaryReport;
